import React, { useState } from 'react';
import { DatoCmsCoverageDetailsGrid } from '../../../schema/datocms';
import { Themes } from '../../../schema/enum/themes';
import { getColumnSpanClass, getColumnStartClass, getTextAlignmentClass } from '../../../utilities';
import HtmlEntityTextComponent from '../../html-entity-text/html-entity-text-component';
import HtmlStringComponent from '../../html-string/html-string-component';
import { useThemeClassMap } from '../../theme/useThemeClassMap';

interface CoverageDisplayData {
    expanded: boolean;
    name: string;
    masterId: string;
    description: string;
    limit: string;
}

const CoverageDetailsGridComponent = (
    {
        columnLayout, title, titleAlignment, product, bottomContentHtml
    }: DatoCmsCoverageDetailsGrid
): React.ReactElement => {
    const initialDisplayCoverages: CoverageDisplayData[] = product.coverages.filter((c) => c.showCoverageOnProductGrid).map((c) => {
        return {
            expanded: false,
            name:  c.coverageNameOverride || c.coverage.name,
            masterId: c.coverage.masterRatingId,
            description: c.descriptionOverride || c.coverage.description,
            limit: c.limit
        };
    });

    const [displayCoverages, setDisplayCoverages] = useState(initialDisplayCoverages);

    function toggleCoverage(coverage: CoverageDisplayData): void {
        const updatedCoverages: CoverageDisplayData[] = [...displayCoverages];
        updatedCoverages[displayCoverages.findIndex(c => c.masterId === coverage.masterId)] = {
            ...coverage,
            expanded: !coverage.expanded
        };

        setDisplayCoverages(updatedCoverages);
    }

    const themeClass = useThemeClassMap(Themes.WHITE);

    const gridColumnSpanClass: string = getColumnSpanClass(columnLayout);
    const gridColumnStartClass: string = getColumnStartClass(columnLayout);

    return (
        <div className={`${ themeClass } page-component`}>
            <div className="container mx-auto lg:grid lg:grid-cols-12">
                <div className={`lg:${gridColumnSpanClass} lg:${gridColumnStartClass}`}>
                    { title && <h2 className={`pl-4 lg:pl-0 ${getTextAlignmentClass(titleAlignment)}`}>
                        { <HtmlEntityTextComponent text={title} /> }
                    </h2> }

                    <div className="hidden lg:flex justify-center py-8">
                        <table className="w-full">
                            { <caption className="hidden">Coverage Details for {product.name}</caption> }

                            <tbody>
                                <tr>
                                    <td className="bg-brand-light-gray font-bold py-2 px-4 text-2xl text-theme-primary-text" colSpan={2}>Coverages</td>
                                </tr>
                                {
                                    displayCoverages.map((mc, index) => {
                                        return (
                                            <React.Fragment key={index}>
                                                <tr className={mc.expanded ? '' : 'border-b bottom-1'}>
                                                    <th scope="row" className={`w-1/2 text-left pt-8 ${mc.expanded ? 'pb-6' : 'pb-8'} px-4`}><button className="text-left text-xl font-bold text-theme-secondary" aria-label="Click to view coverage description" onClick={e => toggleCoverage(mc)}>{mc.name} <span className="text-sm">▼</span></button></th>
                                                    <td className="text-left font-bold">{mc.limit}</td>
                                                </tr>
                                                <tr className={mc.expanded ? 'border-b bottom-1' : 'hidden'}>
                                                    <td className="bg-primary text-white py-6 px-4" colSpan={2}>{mc.description}</td>
                                                </tr>
                                            </React.Fragment>
                                        );
                                    })
                                }
                            </tbody>
                        </table>
                    </div>

                    {
                        <div className="flex flex-col lg:hidden">
                            <div className="bg-brand-light-gray font-bold py-2 px-4 text-theme-primary-text flex justify-between items-center">
                                <span className="text-2xl">Coverages</span>
                            </div>
                            {
                                displayCoverages.map((mc, index) => {
                                    return (
                                        <React.Fragment key={index}>
                                            <div className="border-b bottom-1">
                                                <div className={`pt-8 pb-${mc.expanded ? 6 : 8} px-4 flex flex-col items-start`}>
                                                    <button className="text-left text-xl font-bold text-theme-secondary" aria-label="Click to view coverage description" onClick={e => toggleCoverage(mc)}>{mc.name} <span className="text-sm">▼</span>
                                                    </button>
                                                    <div className="text-left">{mc.limit}</div>
                                                </div>

                                                { mc.expanded &&
                                            <div>
                                                <div className="bg-primary text-white py-6 px-4">{mc.description}</div>
                                            </div>
                                                }
                                            </div>
                                        </React.Fragment>
                                    );
                                })
                            }
                        </div>
                    }
                </div>
            </div>

            { bottomContentHtml && <HtmlStringComponent htmlString={bottomContentHtml} /> }
        </div>
    );
};

export default CoverageDetailsGridComponent;
